// New three-tier pricing modal that opens Stripe Checkout.
// Replaces the legacy local UpgradeModal in KnowMenoApp.tsx.
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { createCheckoutSession } from "@/lib/billing.functions";
import { PLANS, PLAN_ORDER, type PlanId } from "@/lib/plans";
import { supabase } from "@/integrations/supabase/client";
import { getPendingUtm } from "@/lib/utmParams";

const T = "#FFC1CD";
const TL = "#FFE4EA";
const TD = "#4B1F4F";
const RO = "#FF5C7A";
const GR = "#6E4B71";
const GL = "#FAF7F5";
const GD = "#6E4B71";
const WH = "#ffffff";
const PURPLE = "#4B1F4F";

interface Props {
  onClose: () => void;
  trialActive?: boolean;
  trialDaysLeft?: number;
  trialExpired?: boolean;
}

export function BillingModal({ onClose, trialActive = false, trialDaysLeft = 0, trialExpired = false }: Props) {
  const [selected, setSelected] = useState<PlanId>("annual");
  const [withTrial, setWithTrial] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startCheckout = useServerFn(createCheckoutSession);

  const plan = PLANS[selected];

  const handleContinue = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error("You're signed out. Please sign in again to upgrade to Plus.");
      }
      const utm = getPendingUtm();
      const { url } = await startCheckout({ data: { plan: selected, accessToken, withTrial, utm: Object.keys(utm).length > 0 ? utm : undefined } });
      window.location.href = url;
    } catch (e) {
      let msg = "Could not start checkout";
      if (e instanceof Response) {
        msg = `Checkout failed (HTTP ${e.status}). ${e.statusText || ""}`.trim();
        try {
          const txt = await e.text();
          if (txt) msg += ` — ${txt.slice(0, 200)}`;
        } catch { /* ignore */ }
      } else if (e instanceof Error) {
        msg = e.message;
      }
      console.error("[billing] startCheckout failed", e);
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 0,
      }}
      onClick={trialExpired ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: WH,
          borderRadius: "22px 22px 0 0",
          maxWidth: 460,
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          padding: "22px 22px 28px",
          fontFamily: "system-ui, sans-serif",
          color: GD,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: RO, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500 }}>
            Choose your plan
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 22, color: GR, cursor: "pointer", lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: 22, fontWeight: 500, color: GD, letterSpacing: "-0.01em", lineHeight: 1.3, marginBottom: 6 }}>
          {trialExpired ? "Your free month has ended — choose a plan to keep Plus" : "Keep everything KnowMeno does for you."}
        </div>
        <div style={{ fontSize: 13, color: GR, lineHeight: 1.6, marginBottom: 18 }}>
          {trialExpired
            ? "Your data is safe. Subscribe to keep your charts, AI insights, and the Symptom Intelligence Report."
            : "Billed today. Cancel anytime."}
        </div>

        {PLAN_ORDER.map((id) => {
          const plan = PLANS[id];
          const isSelected = selected === id;
          return (
            <div
              key={id}
              onClick={() => setSelected(id)}
              style={{
                position: "relative",
                background: isSelected ? TL : "#FAF7F5",
                border: isSelected ? `2px solid ${T}` : "2px solid transparent",
                borderRadius: 16,
                padding: "16px 18px",
                marginBottom: 10,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {plan.badge && (
                <div
                  style={{
                    position: "absolute",
                    top: -10,
                    right: 14,
                    background: "#FFE4EA",
                    color: "#4B1F4F",
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "4px 10px",
                    borderRadius: 12,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {plan.badge}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? T : "#E8D5DA"}`,
                    background: isSelected ? T : WH,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isSelected && (
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: WH }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 500, color: GD }}>{plan.name}</span>
                    {plan.savings && (
                      <span style={{ fontSize: 11, color: TD, fontWeight: 500 }}>{plan.savings}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 20, fontWeight: 500, color: GD, letterSpacing: "-0.01em" }}>
                      {plan.displayPrice}
                    </span>
                    <span style={{ fontSize: 12, color: GR }}>{plan.cadence}</span>
                    {plan.perMonthEquivalent && (
                      <span style={{ fontSize: 11, color: GR, marginLeft: 4 }}>· {plan.perMonthEquivalent}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div style={{ background: "#FAF7F5", borderRadius: 12, padding: "12px 14px", marginTop: 4, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: GR, lineHeight: 1.6 }}>
            ✓ Doctor reports · ✓ AI insights · ✓ Symptom heatmap · ✓ HRT tracking · ✓ Cycle predictions
          </div>
        </div>

        {!trialExpired && (
          <>
            <div style={{ fontSize: 12, color: GD, fontWeight: 500, marginBottom: 8 }}>How would you like to start?</div>
            {[
              {
                id: false,
                title: "Subscribe and pay now",
                sub: `Start full access immediately. Charged ${plan.displayPrice} today. Cancel anytime.`,
              },
            ].map((opt) => {
              const isSel = withTrial === opt.id;
              return (
                <div
                  key={String(opt.id)}
                  onClick={() => setWithTrial(opt.id)}
                  style={{
                    background: isSel ? TL : "#FAF7F5",
                    border: isSel ? `2px solid ${T}` : "2px solid transparent",
                    borderRadius: 12,
                    padding: "10px 12px",
                    marginBottom: 8,
                    cursor: "pointer",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 18, height: 18, borderRadius: "50%",
                      border: `2px solid ${isSel ? T : "#E8D5DA"}`,
                      background: isSel ? T : WH,
                      flexShrink: 0, marginTop: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {isSel && <div style={{ width: 6, height: 6, borderRadius: "50%", background: WH }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: GD, marginBottom: 2 }}>{opt.title}</div>
                    <div style={{ fontSize: 11, color: GR, lineHeight: 1.5 }}>{opt.sub}</div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {error && (
          <div style={{ background: "#FFE4EA", color: "#4B1F4F", borderRadius: 10, padding: "10px 12px", fontSize: 12, marginBottom: 10, marginTop: 6 }}>
            {error}
          </div>
        )}

        <div style={{ fontSize: 12.5, color: GD, textAlign: "center", lineHeight: 1.55, padding: "10px 12px", background: "#FAF7F5", borderRadius: 10, marginTop: 4, marginBottom: 4 }}>
          You will be billed {plan.displayPrice} {selected === "monthly" ? "every month" : "every year"}. Auto-renews until canceled. Cancel anytime in Settings.
        </div>
        <div style={{ fontSize: 11, color: GR, textAlign: "center", lineHeight: 1.55, marginTop: 8 }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: GR, textDecoration: "underline" }}>Terms</a>
          {" · "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: GR, textDecoration: "underline" }}>Privacy</a>
        </div>
        <button
          onClick={handleContinue}
          disabled={loading}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 16,
            background: T,
            color: WH,
            border: "none",
            fontSize: 15,
            fontWeight: 500,
            cursor: loading ? "wait" : "pointer",
            opacity: loading ? 0.7 : 1,
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          {loading
            ? "Opening secure checkout…"
            : `Pay now — ${plan.displayPrice}`}
        </button>
        <div style={{ fontSize: 11, color: GR, textAlign: "center", lineHeight: 1.55 }}>
          You'll be redirected to Stripe and charged {plan.displayPrice} immediately.
        </div>
      </div>
    </div>
  );
}
